import { IconArrowUp } from "@tabler/icons-react";
import { cva } from "class-variance-authority";
import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";

// Decorative replica of the Juisci GPT composer: the example questions cycle
// through it so a marketing surface can show what the agent is for. Purely
// presentational — the bar is inert (`pointer-events-none`), acting on it is the
// job of the CTAs around it.

// Timings from the Figma timeline: one slot per question, each slot being
// `fade in → hold → fade out`, and the next question starts entering exactly
// when the previous one has finished leaving. Figma's own slot is 1.52s, which
// is under the time it takes to READ a two-line clinical question — the hold is
// stretched here so the questions are legible.
const SLOT_S = 3.5;
const FADE_S = 0.35;

// Questions rise into place and keep rising on the way out.
const TRAVEL_PX = 7;

// Figma's easing curves: expo-out on the way in, expo-in on the way out. Motion
// keeps its `Easing` union internal, so the cubic-bezier shape it accepts is
// spelled out here rather than imported.
type Bezier = readonly [number, number, number, number];
const EASE_IN: Bezier = [0.16, 1, 0.3, 1];
const EASE_OUT: Bezier = [0.7, 0, 0.84, 0];

/** Surface the bar sits on — drives its fill and text contrast. */
export type AskBarTone = "on-image" | "on-surface";

// Height comes from the padding, never from a fixed value: the questions box is
// two lines tall, and Figma's own frame pinned a 44px height over it that its
// `overflow-clip` then hid — which is what made the text look crushed against
// the edges.
const askBarVariants = cva(
  [
    "shadow-ask-bar pointer-events-none flex w-full items-center",
    "overflow-clip rounded-2xl select-none",
  ],
  {
    variants: {
      tone: {
        "on-image": "bg-white/30",
        "on-surface": "bg-white",
      },
      // `desktop` is the desktop sign-in page's (Figma "D7.connexion"): one
      // line of body text and the send button the size of the composer's.
      size: {
        mobile: "gap-2.5 px-3 py-2",
        desktop: "gap-3 py-2.5 pr-2.5 pl-4",
      },
    },
  }
);

const placeholderVariants = cva(
  // Fills the two-line box and centres its content, so the short questions sit
  // on the bar's midline like the long ones.
  "absolute inset-0 flex items-center font-dm-sans",
  {
    variants: {
      tone: {
        "on-image": "text-white",
        "on-surface": "text-ds-slate-500",
      },
      size: {
        mobile: "text-ui-caption",
        desktop: "text-sm/4.5",
      },
    },
  }
);

// Keyframes for the question at `index`, expressed over the whole loop so every
// question runs the same infinite animation and only its `times` differ. The
// first one has no lead-in segment: at t=0 it is already fading in.
function placeholderCycle(
  index: number,
  count: number
): {
  times: number[];
  opacity: number[];
  y: number[];
  ease: (Bezier | "linear")[];
} {
  const loopS = SLOT_S * count;
  const enterStart = (index * SLOT_S) / loopS;
  const enterEnd = (index * SLOT_S + FADE_S) / loopS;
  const exitStart = ((index + 1) * SLOT_S - FADE_S) / loopS;
  const exitEnd = ((index + 1) * SLOT_S) / loopS;

  if (index === 0)
    return {
      times: [0, enterEnd, exitStart, exitEnd, 1],
      opacity: [0, 1, 1, 0, 0],
      y: [TRAVEL_PX, 0, 0, -TRAVEL_PX, -TRAVEL_PX],
      ease: [EASE_IN, "linear", EASE_OUT, "linear"],
    };

  return {
    times: [0, enterStart, enterEnd, exitStart, exitEnd, 1],
    opacity: [0, 0, 1, 1, 0, 0],
    y: [TRAVEL_PX, TRAVEL_PX, 0, 0, -TRAVEL_PX, -TRAVEL_PX],
    ease: ["linear", EASE_IN, "linear", EASE_OUT, "linear"],
  };
}

export function AnimatedAskBar({
  tone,
  size,
}: {
  tone: AskBarTone;
  size: "mobile" | "desktop";
}) {
  const { t } = useTranslation();
  // `null` until the media query resolves — only an explicit opt-out freezes the
  // carousel on its first question.
  const prefersReducedMotion = useReducedMotion();

  const placeholders = [
    t("Is antibiotic prophylaxis always necessary before dental procedures?"),
    t("What evidence supports semaglutide for cardiovascular prevention?"),
    t("What is the eGFR limit for metformin use?"),
    t(
      "Should elderly patients with renal insufficiency receive anticoagulation?"
    ),
    t("What are the latest ESC 2026 recommendations for atrial fibrillation?"),
  ];

  return (
    <div aria-hidden className={cn(askBarVariants({ tone, size }))}>
      <div
        className={cn(
          "relative min-w-0 flex-1",
          size === "mobile" ? "h-8" : "h-9"
        )}
      >
        {placeholders.map((placeholder, index) => {
          const cycle = placeholderCycle(index, placeholders.length);
          return (
            <motion.p
              key={placeholder}
              className={cn(placeholderVariants({ tone, size }))}
              initial={{ opacity: 0, y: TRAVEL_PX }}
              animate={
                prefersReducedMotion === true
                  ? { opacity: index === 0 ? 1 : 0, y: 0 }
                  : { opacity: cycle.opacity, y: cycle.y }
              }
              transition={
                prefersReducedMotion === true
                  ? { duration: 0 }
                  : {
                      duration: SLOT_S * placeholders.length,
                      times: cycle.times,
                      ease: cycle.ease,
                      repeat: Infinity,
                    }
              }
            >
              <span className="line-clamp-2">{placeholder}</span>
            </motion.p>
          );
        })}
      </div>

      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-lg bg-ds-mint-800 text-ds-mint-50",
          size === "mobile" ? "size-7.5" : "size-9"
        )}
      >
        <IconArrowUp className="size-4" stroke={2} />
      </div>
    </div>
  );
}
